import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import PasswordInput from "@/components/PasswordInput";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ password: "", confirm: "", name: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setErr("Missing invite token."); return; }
    api.get(`/invites/${token}`)
      .then(({ data }) => setInvite(data))
      .catch((e) => setErr(formatError(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.password !== form.confirm) {
      setErr("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/invites/accept", {
        token,
        password: form.password,
        name: form.name || undefined,
      });
      setUser(data.user);
      navigate(data.room_id ? `/app/rooms/${data.room_id}` : "/app/rooms", { replace: true });
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  const mismatch = form.confirm && form.password && form.confirm !== form.password;

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-background">
      <ThemeToggle />
      <div className="w-full max-w-md border border-border bg-card p-10">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-primary-foreground" />
          </div>
          <span className="font-display text-base font-medium">Sovereign VDR</span>
        </div>

        {!invite && !err && (
          <div className="label-mono text-muted-foreground">— Verifying invitation —</div>
        )}
        {err && !invite && (
          <>
            <h1 className="font-display text-2xl tracking-tight font-medium">Invitation problem</h1>
            <p className="mt-3 text-sm text-destructive">{err}</p>
            <Link to="/login" className="mt-6 inline-block label-mono text-primary">— Go to sign in</Link>
          </>
        )}
        {invite && (
          <>
            <div className="label-mono text-muted-foreground">
              — {invite.kind === "owner" ? "Owner activation" : "You\u2019re invited"}
            </div>
            <h1 className="mt-3 font-display text-3xl tracking-tight font-medium">
              {invite.kind === "owner" ? "Activate your owner account" : "Accept your invitation"}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {invite.kind === "owner" ? (
                <>Setting up administrator access for <span className="font-mono">{invite.email}</span>. You will have full rights across SovereignVDR.</>
              ) : (
                <>Joining data room <span className="text-foreground font-medium">{invite.room_name}</span> as <span className="font-mono">{invite.email}</span>.</>
              )}
            </p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label className="label-mono">Your name (optional)</label>
                <input
                  data-testid="invite-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 w-full px-3 py-2.5 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              <div>
                <label className="label-mono">Set a password</label>
                <PasswordInput
                  testId="invite-password-input"
                  required minLength={10}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="mt-1"
                />
                <div className="mono text-[10px] text-muted-foreground mt-1">
                  Min 10 chars · upper + lower + digit + symbol.
                </div>
              </div>
              <div>
                <label className="label-mono">Confirm password</label>
                <PasswordInput
                  testId="invite-confirm-input"
                  required minLength={10}
                  value={form.confirm}
                  onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                  className="mt-1"
                />
                {mismatch && (
                  <div className="text-xs text-destructive mt-1">Passwords do not match.</div>
                )}
              </div>
              {err && <div className="text-sm text-destructive">{err}</div>}
              <button
                data-testid="invite-accept-submit"
                type="submit" disabled={busy || mismatch}
                className="w-full px-4 py-2.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition disabled:opacity-50"
              >
                {busy ? "Accepting…" : invite.kind === "owner" ? "Activate & sign in" : "Accept & enter data room"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
